// src/ui/MoneyInput.jsx
// CFO-grade MoneyInput (no TypeScript) using your semantic Tailwind tokens.
// - Wrapper owns border/focus (focus-within ring + offset)
// - Currency prefix on the left (muted)
// - Numeric input right-aligned + tabular-nums
// - Formats on blur, edits raw-ish on focus
// - Locale-aware parsing (group + decimal separators)

import React from "react";

function cx() {
  return Array.prototype.slice.call(arguments).filter(Boolean).join(" ");
}

function clamp(n, min, max) {
  if (typeof min === "number" && n < min) return min;
  if (typeof max === "number" && n > max) return max;
  return n;
}

function getSeparators(locale) {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  const group = (parts.find((p) => p.type === "group") || {}).value || ",";
  const decimal = (parts.find((p) => p.type === "decimal") || {}).value || ".";
  return { group, decimal };
}

function formatCurrency(value, locale, currency, currencyDisplay, decimals) {
  const opts = {
    style: "currency",
    currency,
    currencyDisplay: currencyDisplay || "symbol",
  };
  if (typeof decimals === "number") {
    opts.minimumFractionDigits = decimals;
    opts.maximumFractionDigits = decimals;
  }
  return new Intl.NumberFormat(locale, opts).format(value);
}

function formatNumber(value, locale, decimals) {
  const opts = {};
  if (typeof decimals === "number") {
    opts.minimumFractionDigits = 0;
    opts.maximumFractionDigits = decimals;
  } else {
    opts.maximumFractionDigits = 20;
  }
  return new Intl.NumberFormat(locale, opts).format(value);
}

// Locale-aware parser:
// - Allows digits, whitespace, group sep, decimal sep, +/-
// - Removes grouping, converts decimal sep -> "."
// - Returns number or null
function parseLocalizedNumber(raw, locale, allowNegative, decimals) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (s === "") return null;

  const sep = getSeparators(locale);
  const group = sep.group;
  const decimal = sep.decimal;

  // Normalize non-breaking spaces sometimes used as group separators
  s = s.replace(/\u00A0/g, " ");

  // Keep only: digits, separators, sign, spaces
  const allowed = new RegExp("[^0-9\\s\\" + escapeRegExp(group) + "\\" + escapeRegExp(decimal) + "\\-\\+]", "g");
  s = s.replace(allowed, "");

  // Sign handling
  if (!allowNegative) {
    s = s.replace(/-/g, "");
  }

  // Remove spaces (often grouping)
  s = s.replace(/\s+/g, "");

  // Remove grouping separators
  if (group) {
    const groupRe = new RegExp(escapeRegExp(group), "g");
    s = s.replace(groupRe, "");
  }

  // Convert decimal separator to "."
  if (decimal && decimal !== ".") {
    const decRe = new RegExp(escapeRegExp(decimal), "g");
    // if user typed multiple decimals, keep first and strip the rest
    const first = s.indexOf(decimal);
    if (first !== -1) {
      const before = s.slice(0, first).replace(decRe, "");
      const after = s.slice(first + decimal.length).replace(decRe, "");
      s = before + "." + after;
    }
    s = s.replace(decRe, ".");
  } else {
    // if decimal is ".", still prevent multiple dots
    const parts = s.split(".");
    if (parts.length > 2) {
      s = parts[0] + "." + parts.slice(1).join("");
    }
  }

  // Prevent multiple +/-
  s = s.replace(/\+/g, "");
  // Keep only the first '-' and only at start
  s = s.replace(/(?!^)-/g, "");

  // Decimals limit (truncate, not round, during typing)
  if (typeof decimals === "number") {
    const idx = s.indexOf(".");
    if (idx !== -1) {
      const head = s.slice(0, idx);
      const frac = s.slice(idx + 1).slice(0, Math.max(0, decimals));
      s = head + "." + frac;
    }
  }

  if (s === "" || s === "-" || s === "." || s === "-.") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function escapeRegExp(str) {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * MoneyInput Props (JS):
 * - value: number|null (canonical numeric value)
 * - onChange: (number|null) => void
 * - currency: "EUR" (default)
 * - locale: "nl-NL" (default)
 * - currencyDisplay: "symbol"|"code"|"name"|"narrowSymbol"
 * - placeholder
 * - disabled, readOnly
 * - status: "default"|"warning"|"error"|"success"
 * - allowNegative (default true)
 * - min, max, step, decimals
 * - suffix: ReactNode (e.g. "/ month")
 * - className, inputClassName, prefixClassName
 * - maxWidth: CSS value (e.g. "12rem", "200px")
 */
export function MoneyInput({
  value,
  onChange,

  currency = "EUR",
  locale = "nl-NL",
  currencyDisplay = "symbol",

  placeholder = "",
  disabled = false,
  readOnly = false,

  status = "default",
  allowNegative = true,

  min,
  max,
  step = 1,
  decimals = 2,

  suffix = null,

  id,
  name,
  autoFocus = false,

  className,
  inputClassName,
  prefixClassName,
  maxWidth,

  onBlur,
  onFocus,
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(""); // what user sees while editing

  // Sync draft when not editing
  React.useEffect(() => {
    if (isEditing) return;
    if (value == null) {
      setDraft("");
      return;
    }
    // Show formatted number (not currency) by default in the field; prefix shows currency.
    setDraft(formatNumber(value, locale, decimals));
  }, [value, isEditing, locale, decimals]);

  const isDisabled = disabled;
  const isRO = readOnly;

  // Wrapper styling — state driven
  const wrapperBase =
    "flex items-center gap-2 rounded-md px-3 py-2 " +
    "bg-surface border border-border " +
    "transition-colors duration-150 " +
    "hover:border-border-strong " +
    "focus-within:ring-1 focus-within:ring-ring focus-within:ring-offset-0 focus-within:ring-offset-bg focus-within:border-border-strong";

  const wrapperDisabled =
    "bg-surface-2 text-text-3 cursor-not-allowed opacity-100 hover:border-border";

  const wrapperReadOnly =
    "bg-surface-2 cursor-default hover:border-border";

  const wrapperStatus =
    status === "error"
      ? "border-danger hover:border-danger"
      : status === "warning"
      ? "border-warning hover:border-warning"
      : status === "success"
      ? "border-success hover:border-success"
      : "";

  const prefixCls = cx(
    "text-sm font-medium text-text-3 select-none",
    prefixClassName
  );

  const inputBase =
    "flex-1 min-w-0 bg-transparent text-sm outline-none overflow-hidden text-ellipsis " +
    "text-text placeholder:text-text-3 " +
    "text-right tabular-nums " +
    "disabled:cursor-not-allowed";

  const inputCls = cx(
    inputBase,
    isDisabled ? "text-text-3" : "",
    inputClassName
  );

  const suffixCls = "text-sm text-text-3 whitespace-nowrap select-none";

  function commit(nextNumber, blurEvent) {
    let n = nextNumber;
    if (n != null) {
      if (!allowNegative && n < 0) n = Math.abs(n);
      n = clamp(n, min, max);
      // Round to decimals on commit (blur), to keep canonical stable
      if (typeof decimals === "number") {
        const p = Math.pow(10, decimals);
        n = Math.round(n * p) / p;
      }
    }
    onChange(n);
    setIsEditing(false);

    // After commit, show formatted number
    if (n == null) setDraft("");
    else setDraft(formatNumber(n, locale, decimals));

    if (typeof onBlur === "function" && blurEvent) onBlur(blurEvent);
  }

  function handleFocus(e) {
    if (isDisabled) return;
    setIsEditing(true);

    // On focus: show a raw-ish editable string (no grouping if you prefer).
    // Here we keep locale formatting but it's ok because parser is locale-aware.
    if (value == null) setDraft("");
    else setDraft(formatNumber(value, locale, decimals));

    if (typeof onFocus === "function") onFocus(e);
  }

  function handleChange(e) {
    const nextDraft = e.target.value;
    setDraft(nextDraft);

    // Update canonical value live (finance UIs often do this).
    // If you prefer "commit-on-blur only", remove onChange here.
    const n = parseLocalizedNumber(nextDraft, locale, allowNegative, decimals);
    if (n == null) {
      onChange(null);
      return;
    }
    const clamped = clamp(n, min, max);
    onChange(clamped);
  }

  function handleBlur(e) {
    const n = parseLocalizedNumber(draft, locale, allowNegative, decimals);
    commit(n, e);
  }

  function handleKeyDown(e) {
    if (isDisabled || isRO) return;

    // Enter commits
    if (e.key === "Enter") {
      e.preventDefault();
      const n = parseLocalizedNumber(draft, locale, allowNegative, decimals);
      commit(n, null);
      return;
    }

    // Escape cancels -> revert to last canonical
    if (e.key === "Escape") {
      e.preventDefault();
      setIsEditing(false);
      if (value == null) setDraft("");
      else setDraft(formatNumber(value, locale, decimals));
      e.currentTarget.blur();
      return;
    }

    // ArrowUp/ArrowDown increments (with step)
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      // Only increment when modifier is held, to avoid fighting cursor movement
      // Use Alt+Arrow to step.
      if (!e.altKey) return;
      e.preventDefault();

      const current = parseLocalizedNumber(draft, locale, allowNegative, decimals);
      const base = current == null ? 0 : current;
      const delta = e.key === "ArrowUp" ? step : -step;
      let next = base + delta;

      if (!allowNegative && next < 0) next = 0;
      next = clamp(next, min, max);

      // Keep draft in locale format
      setDraft(formatNumber(next, locale, decimals));
      onChange(next);
    }
  }

  // Currency prefix rendering:
  // Use Intl for symbol if possible; fall back to currency code.
  let currencyPrefix = currency;
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(1);
    const curPart = parts.find((p) => p.type === "currency");
    if (curPart && curPart.value) currencyPrefix = curPart.value;
  } catch (_) {
    currencyPrefix = currency;
  }

  return (
    <div
      className={cx(
        "min-w-0",
        wrapperBase,
        wrapperStatus,
        isDisabled ? wrapperDisabled : "",
        !isDisabled && isRO ? wrapperReadOnly : "",
        className
      )}
      style={maxWidth ? { maxWidth } : undefined}
      aria-disabled={isDisabled ? "true" : undefined}
    >
      <span className={prefixCls}>{currencyPrefix}</span>

      <input
        id={id}
        name={name}
        type="text"
        inputMode="decimal"
        autoFocus={autoFocus}
        className={inputCls}
        placeholder={placeholder}
        value={draft}
        disabled={isDisabled}
        readOnly={isRO}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        aria-invalid={status === "error" ? "true" : undefined}
      />

      {suffix ? <span className={suffixCls}>{suffix}</span> : null}
    </div>
  );
}

// Optional helper for read-only display (currency + formatted number)
// Useful in tables where editing is off:
export function MoneyText({
  value,
  currency = "EUR",
  locale = "nl-NL",
  currencyDisplay = "symbol",
  decimals = 2,
  className,
}) {
  const txt = value == null ? "—" : formatCurrency(value, locale, currency, currencyDisplay, decimals);
  return <span className={cx("tabular-nums text-text", className)}>{txt}</span>;
}
